import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PvReception, Sav, isPoseurTaskType, type Task } from "@relais/contracts";
import { getDb } from "@relais/offline";
import { useOffline } from "../lib/offline-context.js";
import { useSession, useUserProfile } from "../lib/auth.js";
import { PvEditProvider } from "../features/pv-reception/PvEditProvider.js";
import { SubmittedView } from "../features/pv-reception/SubmittedView.js";
import { SavEditProvider } from "../features/sav/SavEditProvider.js";
import { SavSubmittedView } from "../features/sav/SavSubmittedView.js";
import {
  isPvTaskLocked,
  shouldRedirectToFirstStep,
  shouldRedirectToSubmittedView,
} from "../features/pv-reception/submitted-guard.js";

/**
 * Le SAV a son propre wizard ; levée de réserve et PV partagent celui du PV.
 * Les trois vivent sous `/pv/$taskId` : c'est l'URL que le dashboard et le
 * verrou post-soumission connaissent.
 */
function firstStepFor(type: Task["type"]): string {
  return type === "sav" ? Sav.SAV_STEPS[0]!.key : PvReception.PV_RECEPTION_STEPS[0]!.key;
}

export const Route = createFileRoute("/pv/$taskId")({
  // Bare /pv/$taskId redirects to the first wizard step while the task is
  // still editable. Any deep edit-step URL on a submitted task redirects
  // back to bare (which then renders SubmittedView) — see submitted-guard.ts.
  beforeLoad: async ({ params, location }) => {
    const barePath = `/pv/${params.taskId}`;
    const db = getDb();
    const row = await db.withRetry(() => db.tasks.get(params.taskId));
    const task = row?.data;
    // Pas encore connue localement (lien profond ouvert sur un cache froid,
    // typiquement juste après une reconnexion) : on ne peut rien décider ici.
    // `decidedAtLoad: false` dit au composant de rejouer cette décision dès
    // que la tâche remonte du cache — sans ça, l'URL nue reste affichée avec
    // un Outlet sans enfant, donc une page blanche.
    if (!task) return { decidedAtLoad: false };

    const isBare = location.pathname === barePath || location.pathname === `${barePath}/`;
    if (isBare) {
      if (shouldRedirectToFirstStep(task)) {
        throw redirect({
          to: "/pv/$taskId/$step",
          params: { taskId: params.taskId, step: firstStepFor(task.type) },
          replace: true,
        });
      }
      return { decidedAtLoad: true };
    }
    if (shouldRedirectToSubmittedView({ task, pathname: location.pathname, barePath })) {
      throw redirect({ to: "/pv/$taskId", params: { taskId: params.taskId }, replace: true });
    }
    return { decidedAtLoad: true };
  },
  component: PvRoot,
});

function PvRoot() {
  const { t } = useTranslation();
  const { taskId } = Route.useParams();
  const { service, tick } = useOffline();
  const session = useSession();
  const profile = useUserProfile();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { decidedAtLoad } = Route.useRouteContext();
  const [task, setTask] = useState<Task | null>(null);

  // `tick` (bumped once per sync-loop cycle, see offline-context.tsx) is in
  // the deps so an already-mounted wizard re-reads the task from Dexie once
  // background sync confirms a submit — without it, PvRoot only re-fetches
  // on taskId change and the submitted-guard redirect to SubmittedView never
  // fires until a manual reload.
  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    service.getTask(taskId).then((t) => {
      if (!cancelled) setTask(t);
    });
    return () => {
      cancelled = true;
    };
  }, [service, taskId, tick]);

  // Rattrapage du seul cas que `beforeLoad` n'a pas pu trancher : cache froid.
  // Strictement borné à `decidedAtLoad === false`, sinon ce rejeu renverrait
  // aussi sur le wizard le verrouillage post-soumission, qui ramène justement
  // à l'URL nue pour afficher le SubmittedView.
  useEffect(() => {
    if (decidedAtLoad || !task) return;
    const barePath = `/pv/${taskId}`;
    if (pathname !== barePath && pathname !== `${barePath}/`) return;
    if (!shouldRedirectToFirstStep(task)) return;
    navigate({
      to: "/pv/$taskId/$step",
      params: { taskId, step: firstStepFor(task.type) },
      replace: true,
    });
  }, [decidedAtLoad, task, taskId, pathname, navigate]);

  if (session.status !== "authed" || !task) {
    return <p className="text-sm text-muted-foreground">…</p>;
  }

  if (!isPoseurTaskType(task.type)) {
    return <p className="text-sm text-destructive">{t("pv.wrongType")}</p>;
  }

  if (isPvTaskLocked(task)) {
    return task.type === "sav" ? (
      <SavSubmittedView task={task} userId={session.session.user.id} />
    ) : (
      <SubmittedView task={task} userId={session.session.user.id} />
    );
  }

  const teamName =
    profile.status === "ready"
      ? [profile.profile.first_name, profile.profile.last_name].filter(Boolean).join(" ")
      : "";

  if (task.type === "sav") {
    return (
      <SavEditProvider task={task} userId={session.session.user.id}>
        <Outlet />
      </SavEditProvider>
    );
  }

  return (
    <PvEditProvider task={task} userId={session.session.user.id} teamName={teamName}>
      <Outlet />
    </PvEditProvider>
  );
}
